import styled from "styled-components";
import { device } from "@src/theme";

export const ContentWrapper = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.margins.large};
  padding: ${({ theme }) => theme.margins.xl};
  box-sizing: border-box;
  overflow-y: auto;

  @media ${device.mobile} {
    padding: ${({ theme }) => theme.margins.medium};
  }
`;

export const CheckPanel = styled.section`
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.margins.large};
  padding: ${({ theme }) => theme.margins.xl};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  background: linear-gradient(
    145deg,
    ${({ theme }) => theme.colors.gray300} 0%,
    ${({ theme }) => theme.colors.darkBg} 100%
  );
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);

  @media ${device.mobile} {
    padding: ${({ theme }) => theme.margins.mediumL};
  }
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.margins.medium};
`;

export const IconBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.colors.yellow};
  color: ${({ theme }) => theme.colors.black};
`;

export const PanelHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const PanelTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.white};
  letter-spacing: 0.3px;
`;

export const PanelDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.gray150};
`;

export const AdminBadge = styled.span`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${({ theme }) => theme.colors.yellow};
  border: 1px solid ${({ theme }) => theme.colors.yellow};
  background-color: rgba(255, 240, 87, 0.08);
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.margins.medium};
  margin-left: auto;
  flex-shrink: 0;
`;

export const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 0 ${({ theme }) => theme.margins.medium};
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  border-radius: 8px;
  font-family: ${({ theme }) => theme.fonts.main};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.darkerBg};
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.yellow};
    color: ${({ theme }) => theme.colors.yellow};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SectionLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: ${({ theme }) => theme.colors.gray};
`;

export const StatusSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.margins.medium};
`;

export const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.margins.medium};

  @media ${device.tablet}, ${device.laptop} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const StatusCard = styled.div<{ $muted?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: ${({ theme }) => theme.margins.medium};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  background-color: ${({ theme }) => theme.colors.darkerBg};
  opacity: ${({ $muted }) => ($muted ? 0.55 : 1)};
`;

export const StatusName = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${({ theme }) => theme.colors.gray150};
`;

export const StatusValue = styled.span`
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
  color: ${({ theme }) => theme.colors.white};
  font-variant-numeric: tabular-nums;
`;

export const StatusRawKey = styled.code`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.gray};
`;

export const TotalRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.margins.medium};
  padding-top: ${({ theme }) => theme.margins.medium};
  border-top: 1px solid ${({ theme }) => theme.colors.gray400};
`;

export const TotalValue = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.yellow};
  font-variant-numeric: tabular-nums;
`;

export const ErrorMessage = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.red};
`;

export const QueryNote = styled.pre`
  margin: 0;
  padding: ${({ theme }) => theme.margins.medium};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  background-color: ${({ theme }) => theme.colors.darkerBg};
  color: ${({ theme }) => theme.colors.gray150};
  font-size: 12px;
  line-height: 1.5;
  overflow-x: auto;
  white-space: pre;
`;
